import { isAcTerminalId } from "../ac";
import { isSupportedSmartShunt, SMARTSHUNT_SUPPLY } from "../monitor-power-policy";
import type { DefLookup } from "../catalog";
import { buildDcCircuitTopology, type DcSourcePair } from "../dc-circuit-topology";
import { effectiveTerminals } from "../effective";
import { resolveProtectionDevices } from "../protection-devices";
import { isSenseTerminal } from "../sense-terminal";
import type { ComponentNode, Issue, System } from "../types";
import { buildGraph, reachable, v, type Graph, type Vertex } from "./graph";

/** Do not inherit the sizing engine's 12 V fallback as supply evidence.
 * A series bank sums its declared levels; incompatible parallel declarations
 * are unresolved. Converter input/PV voltage is never its output voltage. */
function declaredSourceVoltage(source: DcSourcePair, system: System, lookup: DefLookup): number | undefined {
  const definition = (id: string | undefined) => {
    const node = system.nodes.find(n => n.id === id);
    return node?.kind === "component" ? lookup(node.definitionId, node.customDefinition) : undefined;
  };
  if (source.kind === "converter-output") {
    const def = definition(source.nodeId);
    return def?.outputVoltage ?? def?.voltage;
  }
  let total = 0;
  for (const level of source.levels) {
    const volts = level.map(id => definition(id)?.voltage);
    if (volts.some(value => value == null)) return undefined;
    const reference = volts[0]!;
    if (!Number.isFinite(reference) || reference <= 0 || volts.some(value => value !== reference)) return NaN;
    total += reference;
  }
  return total;
}

/** Victron SmartShunt / SmartShunt IP65 installation §3.4 requires Vbatt+;
 * §3.5 makes AUX optional; §3.7 also allows a branch DC-meter installation.
 * Both manuals' technical data §11.1 specify the supplied 1 A slow-blow lead:
 * https://www.victronenergy.com/media/pg/SmartShunt/en/installation.html
 * https://www.victronenergy.com/media/pg/SmartShunt/en/technical-data.html
 * https://www.victronenergy.com/media/pg/SmartShunt_IP65/en/technical-data.html
 *
 * This checks declared supply wiring/protection, not live power, fuse curves,
 * installation compliance or whole-bank placement. The sense current remains
 * a zero-current leaf in the sizing model. */
export function detectMonitorPower(system: System, lookup: DefLookup): Issue[] {
  const shunts = system.nodes.filter((node): node is ComponentNode => {
    if (node.kind !== "component") return false;
    const def = lookup(node.definitionId, node.customDefinition);
    return def != null && isSupportedSmartShunt(def, node) && system.cables.some(c =>
      c.fromNodeId === node.id || c.toNodeId === node.id);
  });
  if (shunts.length === 0) return [];

  const topology = buildDcCircuitTopology(system, lookup);
  const physical = buildGraph(system, lookup);
  const validPower = new Set<Vertex>();
  for (const node of system.nodes) {
    if (node.kind !== "component") continue;
    const def = lookup(node.definitionId, node.customDefinition);
    if (!def) continue;
    for (const term of effectiveTerminals(def, node)) {
      if (!isAcTerminalId(term.id) && !isSenseTerminal(def, term)) validPower.add(v(node.id, term.id));
    }
  }
  // Use physical device identities, not the effective rating inherited by an
  // adjacent cable: a branch on a holder's INPUT stud has not crossed its fuse.
  const protectedCables = new Set<string>();
  const protectedHolders = new Set<string>();
  const protectedSlots = new Set<Vertex>();
  for (const device of resolveProtectionDevices(system, lookup)) {
    if (device.conflicts.length > 0 || device.ratingA == null || !Number.isFinite(device.ratingA) || device.ratingA <= 0 || device.ratingA > SMARTSHUNT_SUPPLY.fuseA) continue;
    if (device.kind === "cable" || device.kind === "battery-post") {
      for (const id of device.cableIds) protectedCables.add(id);
    } else if (device.kind === "holder") {
      for (const id of device.nodeIds) protectedHolders.add(id);
    } else {
      for (const terminal of device.terminals) {
        if (device.nodeIds.some(id => terminal.startsWith(v(id, "")))) protectedSlots.add(terminal);
      }
    }
  }

  return shunts.flatMap(node => {
    // Only this monitor's Vbatt+ joins the physical power graph. Another
    // monitor's sense terminal, a dangling post, AC wiring or a converter's
    // voltage-producing transition cannot become a supply conductor.
    const positive = v(node.id, "vbat");
    const valid = new Set([...validPower, positive]);
    const supplyGraph: Graph = {
      ...physical,
      adj: new Map([...physical.adj].filter(([vertex]) => valid.has(vertex)).map(([vertex, edges]) =>
        [vertex, edges.filter(edge => valid.has(edge.vertex))],
      )),
    };
    const positiveReach = reachable(supplyGraph, positive);
    const negativeReach = reachable(supplyGraph, v(node.id, "batt-neg"));
    // A source's positive and negative must be physically paired. Converter
    // output pairs also support a DC meter; no whole-bank placement assumed.
    const sources = topology.sources.filter(source =>
      source.positive.some(p => positiveReach.has(p)) && source.negative.some(n => negativeReach.has(n)) &&
      !source.negative.some(n => positiveReach.has(n)));
    let reason = "missing";
    if (sources.length > 0) {
      // A known battery/output reference on these same physical rails can
      // resolve an otherwise unspecified converter output. Conflicting or
      // out-of-range declarations cannot. This checks nominal declarations;
      // charging setpoints/transients still need installation verification.
      const volts = sources.map(source => declaredSourceVoltage(source, system, lookup));
      const known = volts.filter((value): value is number => value != null);
      const voltageUnverified = known.length === 0 || known.some(value => !Number.isFinite(value) || value < SMARTSHUNT_SUPPLY.minimumV || value > SMARTSHUNT_SUPPLY.maximumV);
      // Remove every adequate declared fuse. Any surviving path to a paired
      // source is a bypass, so one correctly fused branch cannot hide it.
      const withoutProtection: Graph = {
        ...supplyGraph,
        adj: new Map([...supplyGraph.adj].map(([vertex, edges]) => [vertex, edges.filter(({ via }) => {
          if (via.kind === "cable") return !protectedCables.has(via.cableId);
          if (via.link.kind !== "fused-slot") return true;
          return !protectedHolders.has(via.link.nodeId) &&
            !protectedSlots.has(v(via.link.nodeId, via.link.from)) &&
            !protectedSlots.has(v(via.link.nodeId, via.link.to));
        })])),
      };
      const unprotectedReach = reachable(withoutProtection, positive);
      const protectionUnverified = sources.some(source => source.positive.some(p => unprotectedReach.has(p)));
      if (!voltageUnverified && !protectionUnverified) return [];
      reason = voltageUnverified ? "voltage" : "protection";
    }
    const def = lookup(node.definitionId, node.customDefinition)!;
    const vars = { label: node.label ?? def.shortName ?? def.name };
    return [{
      code: "monitor-power-unverified", severity: "warning",
      title: { key: "diag.monitor-power-unverified.title", vars },
      message: { key: `diag.monitor-power-unverified.message_${reason}`, vars },
      fix: { key: "diag.monitor-power-unverified.fix", vars },
      nodeIds: [node.id],
      cableIds: system.cables.filter(c => c.fromNodeId === node.id || c.toNodeId === node.id).map(c => c.id),
    }];
  });
}
